import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidCoordinate, looksJammed, getReferencePoint, loadLeaflet, captureDeviceGps } from '../utils/geo';
import { addBaseLayer } from '../utils/offline/baseLayer';

export default function MapGpsPicker({ lat, lng, onChange, onPinDropped, hintText, showRecapture = true }) {
  const { t } = useTranslation('collections');
  const [showMap, setShowMap] = useState(false);
  const [gpsStatus, setGpsStatus] = useState('idle');
  const mapContainerRef  = useRef(null);
  const leafletMapRef    = useRef(null);
  const leafletMarkerRef = useRef(null);
  const mountedRef       = useRef(true);

  // Status is fully derived from lat/lng, except the transient 'loading'/'error'
  // states captureGPS sets directly during an in-flight device-location request —
  // those don't change lat/lng, so this effect never clobbers them.
  useEffect(() => {
    if (!lat || !lng) { setGpsStatus('idle'); return; }
    // Three states, not two. 'invalid' is a coordinate that cannot exist and is
    // always an input error; 'warning' is a valid coordinate that is
    // implausibly far from where this device has recently been, which is what
    // GPS jamming looks like. The old check collapsed both into "outside
    // Israel", which stops meaning anything once specimens come from anywhere.
    if (!isValidCoordinate(lat, lng)) { setGpsStatus('invalid'); return; }
    setGpsStatus(looksJammed(lat, lng) ? 'warning' : 'ok');
  }, [lat, lng]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!showMap || !mapContainerRef.current || !window.L) return;
    const L = window.L;
    // Where to open the map when there is no pin yet. Was a hard-coded
    // 31.5/35.0 at country zoom -- central Israel -- which is unhelpful
    // anywhere else and quietly suggests the app expects a location.
    // Preference: the existing pin, then wherever this device last was, then
    // the whole world.
    const reference = getReferencePoint();
    let initLat = parseFloat(lat);
    let initLng = parseFloat(lng);
    let initZoom = 10;
    if (Number.isNaN(initLat) || Number.isNaN(initLng)) {
      if (reference) {
        initLat = reference.lat; initLng = reference.lng; initZoom = 9;
      } else {
        initLat = 20; initLng = 0; initZoom = 2;
      }
    }
    const map = L.map(mapContainerRef.current).setView([initLat, initLng], initZoom);
    addBaseLayer(L, map, { attribution: '© OpenStreetMap contributors', maxZoom: 18 });
    leafletMapRef.current = map;
    if (lat && lng) {
      leafletMarkerRef.current = L.marker([initLat, initLng]).addTo(map);
    }
    map.on('click', (e) => {
      const { lat: clickLat, lng: clickLng } = e.latlng;
      if (leafletMarkerRef.current) {
        leafletMarkerRef.current.setLatLng([clickLat, clickLng]);
      } else {
        leafletMarkerRef.current = L.marker([clickLat, clickLng]).addTo(map);
      }
      const fixedLat = clickLat.toFixed(6);
      const fixedLng = clickLng.toFixed(6);
      onChange(fixedLat, fixedLng);
      if (onPinDropped) onPinDropped(fixedLat, fixedLng);
    });
    return () => {
      map.remove();
      leafletMapRef.current = null;
      leafletMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  const openMap = async () => {
    if (showMap) { setShowMap(false); return; }
    try {
      await loadLeaflet();
      setShowMap(true);
    } catch { /* Leaflet CDN unavailable — fail silently */ }
  };

  const captureGPS = () => {
    setGpsStatus('loading');
    captureDeviceGps()
      .then(({ lat: newLat, lng: newLng }) => { if (mountedRef.current) onChange(newLat, newLng); })
      .catch(() => { if (mountedRef.current) setGpsStatus('error'); });
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    if (name === 'gps_lat') onChange(value, lng);
    else onChange(lat, value);
  };

  const GPS_LABEL = { idle: t('step2.gpsIdle'), loading: t('step2.gpsLoading'), ok: t('step2.gpsOk'), warning: t('step2.gpsWarning'), invalid: t('step2.gpsInvalid'), error: t('step2.gpsError') };
  const GPS_COLOR = { idle: '', loading: 'var(--c-text-muted)', ok: 'var(--c-success)', warning: 'var(--c-warning-text-2)', invalid: 'var(--c-danger-2)', error: 'var(--c-danger-2)' };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
        {t('step2.gpsLabel')}
        {showRecapture && (
          <button type="button" onClick={captureGPS} title={t('common:tip.gpsRecapture')}
            style={{ ...btnStyle('var(--c-info)'), marginInlineStart: 10, padding: '4px 10px', fontSize: 12 }}>
            {t('step2.recapture')}
          </button>
        )}
        <button type="button" onClick={openMap} title={showMap ? t('common:tip.gpsMapClose') : t('common:tip.gpsMapOpen')}
          style={{ ...btnStyle(showMap ? 'var(--c-text-body)' : 'var(--c-text-muted)'), marginInlineStart: 8, padding: '4px 10px', fontSize: 12 }}>
          {showMap ? t('step2.closeMap') : t('step2.dropPin')}
        </button>
      </div>
      {gpsStatus !== 'idle' && (
        <p style={{ fontSize: 13, color: GPS_COLOR[gpsStatus], margin: '0 0 8px' }}>{GPS_LABEL[gpsStatus]}</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
        <label style={labelStyle}>{t('step2.latitude')}
          <input name="gps_lat" type="number" step="any" value={lat} onChange={handleManualChange} placeholder="e.g. 31.7683" title={t('common:tip.gpsLat')} style={inputStyle} />
        </label>
        <label style={labelStyle}>{t('step2.longitude')}
          <input name="gps_lng" type="number" step="any" value={lng} onChange={handleManualChange} placeholder="e.g. 35.2137" title={t('common:tip.gpsLng')} style={inputStyle} />
        </label>
      </div>

      {showMap && (
        <div style={{ marginBottom: 10 }}>
          {hintText && <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '0 0 6px' }}>{hintText}</p>}
          <div ref={mapContainerRef} style={{ height: 320, borderRadius: 8, border: '1px solid var(--c-border-strong)', overflow: 'hidden' }} />
          {lat && (
            <p style={{ fontSize: 12, color: 'var(--c-success-fg)', margin: '6px 0 0' }}>
              {t('step2.pinLabel', { lat: parseFloat(lat).toFixed(5), lng: parseFloat(lng).toFixed(5) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500 };
const inputStyle = { padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14, fontWeight: 400 };
const btnStyle = (bg) => ({ background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14 });
